import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MapIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CloudArrowDownIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchTenantTicketingRoutes,
  updateTenantTicketingRoute,
  removeTenantTicketingRoute,
  fetchTicketingOptions,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { SEVERITY_OPTIONS, severityLabel } from '../data/severities';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy HH:mm'); } catch { return dateStr; }
}

const EDIT_FIELDS = ['companyId', 'boardOrQueueId', 'newStatusId', 'completedStatusId'];

// Normalize a priorityMap object to a full {severity: id} shape (missing → '').
function normPriorityMap(pm) {
  const out = {};
  SEVERITY_OPTIONS.forEach((s) => { out[s] = `${(pm && pm[s] != null) ? pm[s] : ''}`; });
  return out;
}
function countMapped(pm) {
  return SEVERITY_OPTIONS.filter((s) => pm && `${pm[s] || ''}`.trim()).length;
}

// Status-cache key. ConnectWise statuses are board-scoped; AutoTask's are a flat
// global picklist, so every AutoTask row of an MSP shares one cache entry.
function statusCacheKey(mspCode, system, boardId) {
  return system === 'autotask' ? `${mspCode}|AT` : `${mspCode}|${boardId || ''}`;
}

// Render a PSA option dropdown. Falls back to the current stored id (so a saved
// value is preserved/visible even before "Fetch from PSA" has run).
function OptionSelect({ value, options, disabled, onChange, placeholder }) {
  const hasOptions = Array.isArray(options) && options.length > 0;
  const currentInList = hasOptions && options.some((o) => `${o.id}` === `${value || ''}`);
  return (
    <select
      value={`${value || ''}`}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="input-field w-full text-xs disabled:opacity-60"
    >
      <option value="">{placeholder || '— select —'}</option>
      {value && !currentInList && <option value={`${value}`}>{`current: ${value}`}</option>}
      {hasOptions && options.map((o) => (
        <option key={`${o.id}`} value={`${o.id}`}>{o.name ? `${o.name} (${o.id})` : `${o.id}`}</option>
      ))}
    </select>
  );
}

export default function TenantTicketingRoutingManager() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const isMspAdmin = !!user?.isMspAdmin;
  const adminMspCodes = useMemo(
    () => (user?.adminMspCodes || []).map((c) => `${c}`.toUpperCase()),
    [user]
  );
  const canEdit = useCallback(
    (mspCode) => isGlobalAdmin || adminMspCodes.includes(`${mspCode || ''}`.toUpperCase()),
    [isGlobalAdmin, adminMspCodes]
  );
  const anyAdmin = isGlobalAdmin || isMspAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [edits, setEdits] = useState({});      // tenantId -> { companyId, ... , enabled }
  // PSA option caches are MSP-scoped: companies/boards/priorities are identical for
  // every tenant under an MSP, so one "Fetch from PSA" serves all that MSP's rows
  // (no re-fetch when editing the next client). Statuses are board-scoped (CW) or
  // global (AutoTask), cached by key.
  const [mspOptions, setMspOptions] = useState({});       // mspCode -> { companies, boards, priorities }
  const [statusOptions, setStatusOptions] = useState({}); // `${mspCode}|${boardKey}` -> [ {id,name} ]
  const [companyFilter, setCompanyFilter] = useState({}); // tenantId -> filter text
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [fetchingId, setFetchingId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [rowMsg, setRowMsg] = useState({}); // tenantId -> { ok, message }

  const [mspFilter, setMspFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenantTicketingRoutes();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const seed = {};
      list.forEach((r) => {
        seed[r.tenantId] = {
          companyId: r.companyId || '',
          boardOrQueueId: r.boardOrQueueId || '',
          newStatusId: r.newStatusId || '',
          completedStatusId: r.completedStatusId || '',
          priorityMap: normPriorityMap(r.priorityMap),
          enabled: !!r.enabled,
        };
      });
      setEdits(seed);
    } catch (err) {
      setError(err.message || 'Failed to load tenant ticketing routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setEdit = (id, patch) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const isDirty = useCallback((r) => {
    const e = edits[r.tenantId];
    if (!e) return false;
    if (e.enabled !== !!r.enabled) return true;
    if (EDIT_FIELDS.some((f) => (e[f] || '') !== (r[f] || ''))) return true;
    const savedPm = normPriorityMap(r.priorityMap);
    const editPm = e.priorityMap || {};
    return SEVERITY_OPTIONS.some((s) => `${editPm[s] || ''}` !== `${savedPm[s] || ''}`);
  }, [edits]);

  const mspCodes = useMemo(() => {
    const set = new Set(rows.map((r) => r.mspCode).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (mspFilter !== 'all' && r.mspCode !== mspFilter) return false;
      if (!q) return true;
      return `${r.friendlyName} ${r.tenantId} ${r.mspCode}`.toLowerCase().includes(q);
    });
  }, [rows, mspFilter, search]);

  const configuredCount = useMemo(() => rows.filter((r) => r.enabled).length, [rows]);

  // Fetch companies/boards/priorities for the MSP once and cache MSP-wide, so the
  // next client under the same MSP shows the dropdowns without re-fetching. Statuses
  // are cached globally for AutoTask, by board for ConnectWise.
  const handleFetch = async (r) => {
    setRowError((prev) => ({ ...prev, [r.tenantId]: null }));
    setRowMsg((prev) => ({ ...prev, [r.tenantId]: null }));
    if (!r.credentialsConfigured) {
      setRowMsg((prev) => ({ ...prev, [r.tenantId]: { ok: false, message: `Save ${r.mspCode} ConnectWise/AutoTask credentials on the PSA Configuration page first.` } }));
      return;
    }
    setFetchingId(r.tenantId);
    try {
      const [companies, boards, priorities] = await Promise.all([
        fetchTicketingOptions(r.mspCode, 'companies'),
        fetchTicketingOptions(r.mspCode, 'boards'),
        fetchTicketingOptions(r.mspCode, 'priorities'),
      ]);
      const mo = {
        companies: companies.options || [],
        boards: boards.options || [],
        priorities: priorities.options || [],
      };
      setMspOptions((prev) => ({ ...prev, [r.mspCode]: mo }));
      // Statuses: AutoTask is a global picklist (fetch once, MSP-wide); ConnectWise
      // needs the currently-selected board.
      let statusCount = 0;
      if (r.ticketingSystem === 'autotask') {
        try {
          const st = await fetchTicketingOptions(r.mspCode, 'statuses');
          setStatusOptions((prev) => ({ ...prev, [statusCacheKey(r.mspCode, 'autotask')]: st.options || [] }));
          statusCount = (st.options || []).length;
        } catch { /* statuses optional */ }
      } else {
        const boardId = edits[r.tenantId]?.boardOrQueueId;
        if (boardId) {
          try {
            const st = await fetchTicketingOptions(r.mspCode, 'statuses', boardId);
            setStatusOptions((prev) => ({ ...prev, [statusCacheKey(r.mspCode, 'connectwise', boardId)]: st.options || [] }));
            statusCount = (st.options || []).length;
          } catch { /* statuses optional */ }
        }
      }
      const queueWord = r.ticketingSystem === 'autotask' ? 'queues' : 'boards';
      setRowMsg((prev) => ({ ...prev, [r.tenantId]: { ok: true, message: `Loaded ${mo.companies.length} companies, ${mo.boards.length} ${queueWord}, ${statusCount} statuses, ${mo.priorities.length} priorities — cached for all ${r.mspCode} tenants.` } }));
    } catch (err) {
      setRowMsg((prev) => ({ ...prev, [r.tenantId]: { ok: false, message: err.message || 'Failed to fetch options from PSA' } }));
    } finally {
      setFetchingId(null);
    }
  };

  const handleBoardChange = async (r, boardId) => {
    // CW statuses are board-scoped → reset the picked statuses on board change.
    // AutoTask statuses are global → keep them when the queue changes.
    const patch = { boardOrQueueId: boardId };
    if (r.ticketingSystem !== 'autotask') { patch.newStatusId = ''; patch.completedStatusId = ''; }
    setEdit(r.tenantId, patch);
    if (!boardId || r.ticketingSystem === 'autotask') return;
    const key = statusCacheKey(r.mspCode, 'connectwise', boardId);
    if (statusOptions[key]) return; // already cached for this board
    try {
      const st = await fetchTicketingOptions(r.mspCode, 'statuses', boardId);
      setStatusOptions((prev) => ({ ...prev, [key]: st.options || [] }));
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.tenantId]: err.message || 'Failed to load statuses for board' }));
    }
  };

  // Expand/collapse a row. On first open of an unconfigured tenant, prefill its
  // routing from the MSP defaults (queue/status/priority) — Company stays blank
  // (per-client). Values show as "current: <id>" until "Fetch from PSA" loads labels.
  const handleExpand = (r) => {
    const opening = expanded !== r.tenantId;
    setExpanded(opening ? r.tenantId : null);
    if (!opening || r.configured || !r.mspDefaults) return;
    setEdits((prev) => {
      const cur = prev[r.tenantId] || {};
      const alreadyTouched = (cur.boardOrQueueId || cur.newStatusId || cur.completedStatusId || countMapped(cur.priorityMap) > 0);
      if (alreadyTouched) return prev;
      const d = r.mspDefaults;
      return {
        ...prev,
        [r.tenantId]: {
          ...cur,
          boardOrQueueId: `${d.boardOrQueueId || ''}`,
          newStatusId: `${d.newStatusId || ''}`,
          completedStatusId: `${d.completedStatusId || ''}`,
          priorityMap: normPriorityMap(d.priorityMap),
        },
      };
    });
  };

  const handleSave = async (r) => {
    const e = edits[r.tenantId];
    if (!e) return;
    setRowError((prev) => ({ ...prev, [r.tenantId]: null }));
    setRowMsg((prev) => ({ ...prev, [r.tenantId]: null }));
    if (e.enabled && (!e.companyId || !e.boardOrQueueId || !e.newStatusId)) {
      setRowError((prev) => ({ ...prev, [r.tenantId]: 'Company, Board/Queue, and New Status are required to enable routing.' }));
      return;
    }
    setSavingId(r.tenantId);
    try {
      const updated = await updateTenantTicketingRoute(r.tenantId, {
        companyId: e.companyId, boardOrQueueId: e.boardOrQueueId, newStatusId: e.newStatusId,
        completedStatusId: e.completedStatusId, priorityMap: e.priorityMap || {}, enabled: e.enabled,
      });
      setRows((prev) => prev.map((item) => (item.tenantId === r.tenantId ? { ...item, ...updated, ticketingSystem: item.ticketingSystem, credentialsConfigured: item.credentialsConfigured } : item)));
      setRowMsg((prev) => ({ ...prev, [r.tenantId]: { ok: true, message: 'Saved.' } }));
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.tenantId]: err.message || 'Failed to save' }));
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async (r) => {
    if (!window.confirm(`Remove ticketing routing for ${r.friendlyName || r.tenantId}? This tenant's alerts will stop opening PSA tickets (Teams/email unaffected).`)) return;
    setRowError((prev) => ({ ...prev, [r.tenantId]: null }));
    setRemovingId(r.tenantId);
    try {
      await removeTenantTicketingRoute(r.tenantId);
      const cleared = { companyId: '', boardOrQueueId: '', newStatusId: '', completedStatusId: '', priorityMap: normPriorityMap(null), enabled: false };
      setRows((prev) => prev.map((item) => (item.tenantId === r.tenantId ? { ...item, ...cleared, priorityMap: {}, configured: false } : item)));
      setEdit(r.tenantId, cleared);
      setRowMsg((prev) => ({ ...prev, [r.tenantId]: { ok: true, message: 'Routing removed.' } }));
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.tenantId]: err.message || 'Failed to remove' }));
    } finally {
      setRemovingId(null);
    }
  };

  const boardLabel = (sys) => (sys === 'autotask' ? 'Queue' : 'Board');

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <MapIcon className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-100">Tenant Ticketing Routing</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-tenant PSA routing IDs (client company, board/queue, statuses, priority). The dispatcher opens
          a ticket only when the MSP's ticketing is enabled AND a tenant has an enabled routing row — these
          IDs override the MSP credential bundle's single client. Use <span className="font-semibold">Fetch from PSA</span> to
          pick real values from ConnectWise/AutoTask.
        </p>
      </div>

      {!anyAdmin && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to change routing.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <select value={mspFilter} onChange={(e) => setMspFilter(e.target.value)} className="input-field text-xs">
          {mspCodes.map((c) => <option key={c} value={c}>{c === 'all' ? 'All MSPs' : c}</option>)}
        </select>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tenant / id / MSP" className="input-field text-xs flex-1 min-w-[180px]" />
        <span className="text-xs text-gray-500 ml-auto">{visibleRows.length} tenant{visibleRows.length === 1 ? '' : 's'} · {configuredCount} routed</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-gray-800">
                {['', 'Tenant', 'MSP', 'System', 'Company', 'Board/Queue', 'New', 'Completed', 'Priority', 'Enabled', ''].map((label, i) => (
                  <th key={i} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3 whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr><td colSpan={11} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
                    <span className="text-gray-500 text-sm">Loading tenant routing...</span>
                  </div>
                </td></tr>
              )}

              {!loading && visibleRows.length === 0 && (
                <tr><td colSpan={11} className="px-6 py-12 text-center">
                  <MapIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 font-medium">No tenants</p>
                </td></tr>
              )}

              {!loading && visibleRows.map((r) => {
                const e = edits[r.tenantId] || { companyId: '', boardOrQueueId: '', newStatusId: '', completedStatusId: '', priorityMap: normPriorityMap(null), enabled: false };
                const editable = canEdit(r.mspCode);
                const isOpen = expanded === r.tenantId;
                // MSP-scoped option cache + status cache (by board for CW, global for AT).
                const mo = mspOptions[r.mspCode] || {};
                const statusList = statusOptions[statusCacheKey(r.mspCode, r.ticketingSystem, e.boardOrQueueId)] || [];
                const opt = { companies: mo.companies, boards: mo.boards, priorities: mo.priorities, statuses: statusList };
                const dirty = isDirty(r);
                const saving = savingId === r.tenantId;
                const removing = removingId === r.tenantId;
                const fetching = fetchingId === r.tenantId;
                const disabledSys = (r.ticketingSystem === 'disabled' || !r.ticketingSystem);
                const msg = rowMsg[r.tenantId];
                const cFilter = (companyFilter[r.tenantId] || '').toLowerCase();
                const filteredCompanies = (opt.companies || []).filter((o) => !cFilter || `${o.name} ${o.id}`.toLowerCase().includes(cFilter));
                return (
                  <React.Fragment key={r.tenantId}>
                    <tr className="hover:bg-gray-800/50 transition-colors align-top">
                      <td className="px-2 py-3 w-8">
                        <button onClick={() => handleExpand(r)} className="p-0.5 text-gray-500 hover:text-blue-400" title={isOpen ? 'Collapse' : 'Configure routing'}>
                          {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-gray-200">{r.friendlyName || '—'}</div>
                        <div className="text-[11px] text-gray-600 font-mono truncate max-w-[200px]" title={r.tenantId}>{r.tenantId}</div>
                      </td>
                      <td className="px-3 py-3 font-mono text-gray-300">{r.mspCode}</td>
                      <td className="px-3 py-3 text-xs">
                        {disabledSys ? <span className="text-gray-600">disabled</span> : <span className="text-gray-300">{r.ticketingSystem}</span>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-400 font-mono">{r.companyId || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-400 font-mono">{r.boardOrQueueId || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-400 font-mono">{r.newStatusId || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-400 font-mono">{r.completedStatusId || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-400">
                        {countMapped(r.priorityMap) > 0
                          ? <span className="text-sky-400" title="Per-severity priorities mapped">{countMapped(r.priorityMap)} mapped</span>
                          : '—'}
                      </td>
                      <td className="px-3 py-3">
                        {r.enabled
                          ? <span className="inline-flex items-center gap-1 text-green-400 text-xs"><CheckCircleIcon className="h-4 w-4" /> On</span>
                          : <span className="text-gray-600 text-xs">Off</span>}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        {editable && r.configured && (
                          <button onClick={() => handleRemove(r)} disabled={removing} title="Remove routing" className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center disabled:opacity-40">
                            {removing ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <TrashIcon className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-gray-900/40">
                        <td></td>
                        <td colSpan={10} className="px-3 py-4">
                          {disabledSys && (
                            <div className="mb-3 text-xs text-amber-400 inline-flex items-center gap-1">
                              <ExclamationTriangleIcon className="h-4 w-4" /> {r.mspCode} has no ticketing system configured — set it on the PSA Configuration page first.
                            </div>
                          )}
                          <div className="flex items-center gap-3 mb-3 flex-wrap">
                            {editable && (
                              <button onClick={() => handleFetch(r)} disabled={fetching || disabledSys} className="btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                                {fetching ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CloudArrowDownIcon className="h-3.5 w-3.5" />}
                                {mo.companies?.length ? 'Refresh from PSA' : 'Fetch from PSA'}
                              </button>
                            )}
                            {msg && (
                              <span className={`text-xs inline-flex items-center gap-1 ${msg.ok ? 'text-green-400' : 'text-red-400'}`}>
                                {msg.ok ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}{msg.message}
                              </span>
                            )}
                            {!msg && mo.companies?.length > 0 && (
                              <span className="text-xs inline-flex items-center gap-1 text-gray-500">
                                <CheckCircleIcon className="h-4 w-4 text-green-500" />Using cached {r.mspCode} PSA options
                              </span>
                            )}
                          </div>
                          {!r.configured && r.mspDefaults && (
                            <p className="text-[11px] text-sky-400/80 mb-3 -mt-1">
                              Prefilled from {r.mspCode} MSP defaults — pick a Client Company and Save.
                            </p>
                          )}

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
                            <div className="lg:col-span-1">
                              <label className="block text-[11px] text-gray-500 mb-1">Client Company</label>
                              {(opt.companies || []).length > 5 && (
                                <input type="text" value={companyFilter[r.tenantId] || ''} onChange={(ev) => setCompanyFilter((p) => ({ ...p, [r.tenantId]: ev.target.value }))} placeholder="filter companies…" className="input-field w-full text-xs mb-1" />
                              )}
                              <OptionSelect value={e.companyId} options={filteredCompanies} disabled={!editable} onChange={(v) => setEdit(r.tenantId, { companyId: v })} placeholder="— select company —" />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">{boardLabel(r.ticketingSystem)}</label>
                              <OptionSelect value={e.boardOrQueueId} options={opt.boards} disabled={!editable} onChange={(v) => handleBoardChange(r, v)} placeholder={`— select ${boardLabel(r.ticketingSystem).toLowerCase()} —`} />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">New (open) Status</label>
                              <OptionSelect value={e.newStatusId} options={opt.statuses} disabled={!editable} onChange={(v) => setEdit(r.tenantId, { newStatusId: v })} placeholder={opt.statuses?.length ? '— select status —' : 'pick a board/queue first'} />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">Completed (close) Status</label>
                              <OptionSelect value={e.completedStatusId} options={opt.statuses} disabled={!editable} onChange={(v) => setEdit(r.tenantId, { completedStatusId: v })} placeholder={opt.statuses?.length ? '— select status —' : 'pick a board/queue first'} />
                            </div>
                          </div>

                          {/* Per-severity priority mapping — alert severity → PSA priority.
                              Unmapped severities fall back to Default Priority above. */}
                          <div className="mt-4 pt-3 border-t border-gray-800/60">
                            <div className="text-[11px] text-gray-500 mb-2">
                              Priority mapping — map each alert severity to a PSA priority. Unmapped severities use Default Priority.
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-4xl">
                              {SEVERITY_OPTIONS.map((sev) => (
                                <div key={sev}>
                                  <label className="block text-[11px] text-gray-500 mb-1">{severityLabel(sev)}</label>
                                  <OptionSelect
                                    value={(e.priorityMap || {})[sev] || ''}
                                    options={opt.priorities}
                                    disabled={!editable}
                                    onChange={(v) => setEdit(r.tenantId, { priorityMap: { ...(e.priorityMap || {}), [sev]: v } })}
                                    placeholder="— default —"
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Action row — routing on/off toggle sits with Save. */}
                          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-800/60">
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input type="checkbox" checked={e.enabled} disabled={!editable} onChange={(ev) => setEdit(r.tenantId, { enabled: ev.target.checked })} className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 disabled:opacity-40" />
                              <span className={`text-xs font-medium ${e.enabled ? 'text-green-400' : 'text-gray-500'}`}>{e.enabled ? 'Routing enabled' : 'Routing disabled'}</span>
                            </label>
                            {editable && (
                              <button onClick={() => handleSave(r)} disabled={!dirty || saving} className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                                {saving ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckIcon className="h-3.5 w-3.5" />}
                                Save
                              </button>
                            )}
                            <span className="text-[11px] text-gray-600 ml-auto">Updated {formatDate(r.updatedUtc)}{r.updatedBy ? ` · ${r.updatedBy}` : ''}</span>
                          </div>
                          {rowError[r.tenantId] && <p className="mt-2 text-xs text-red-400">{rowError[r.tenantId]}</p>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
